// ABOUTME: Error boundary component for graceful error handling
// ABOUTME: Catches React errors and displays fallback UI

import { ArrowPathIcon, ExclamationTriangleIcon, HomeIcon } from '@heroicons/react/24/outline'
import React, { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error boundary caught error:', error, errorInfo)
    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-bg">
          <div className="bg-surface rounded-lg shadow-md p-12 max-w-2xl w-full text-center">
            <div className="inline-block p-4 rounded-full bg-error-100 dark:bg-error-900/30 mb-8">
              <ExclamationTriangleIcon className="w-16 h-16 text-error-600" />
            </div>

            <h1 className="text-4xl font-semibold tracking-tight text-fg-primary mb-4">
              Oops! Something went wrong
            </h1>

            <p className="text-fg-secondary mb-8">
              We encountered an unexpected error. Don't worry, your data is safe.
            </p>

            {this.state.error && import.meta.env.DEV && (
              <div className="bg-surface rounded-lg shadow-md p-4 mb-8 text-left">
                <p className="text-sm font-mono text-error-700 dark:text-error-300 mb-4">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <details className="text-xs text-fg-secondary">
                    <summary className="cursor-pointer hover:text-fg-primary">Stack trace</summary>
                    <pre className="mt-2 overflow-auto p-2 bg-surface rounded">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={this.handleReset}
                className="btn-accent inline-flex items-center justify-center gap-2 py-3"
              >
                <ArrowPathIcon className="w-5 h-5" />
                Try Again
              </button>

              <button
                type="button"
                onClick={this.handleGoHome}
                className="btn-primary inline-flex items-center justify-center gap-2 py-3"
              >
                <HomeIcon className="w-5 h-5" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Hook-based error boundary for functional components
 * Use this in route definitions
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}
